import { useCallback, useState } from 'react'

/**
 * Radix menu/select Roots manage their own open state, but Motion exit
 * animations need that state lifted so the Content can stay mounted through
 * the exit (via `forceMount` + `AnimatePresence`). This mirrors Radix's own
 * `useControllableState`: a consumer-supplied `open` prop wins when defined,
 * otherwise we track it internally, seeded from `defaultOpen`. Either way we
 * expose the resolved value + a setter that also forwards to the consumer's
 * `onOpenChange`, so the public component API is unchanged.
 */
export function useControllableOpen({
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
}: {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}): [boolean, (open: boolean) => void] {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false)

  const isControlled = controlledOpen !== undefined

  const open = isControlled ? controlledOpen : uncontrolledOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next)
      }

      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  return [open, setOpen]
}
